import { useState } from 'react';
import { ZonedDateTime } from '@js-joda/core';
import {
  Card,
  Flex,
  Text,
  FlexProps,
  Box,
  Image,
  H4,
  Button,
  Divider,
  Icon,
  Loader,
} from '@opendoor/bricks/core';
import ImgixImg from '@opendoor/bricks/utils/ImgixImg';
import { getRailsEnv } from '@opendoor/shared-fe/build/railsEnv';
import { useObservability } from 'shared/helpers/observability';
import { useSupportContacts } from 'sellDirect/helpers/useSupportContacts';

import { ConfirmationModal } from 'sellDirect/components/legacy/sellerDash/ConfirmationModal';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { useOfferContext } from 'sellDirect/OfferContext';
import { AGENT_ACQ_EMAIL } from 'shared/helpers/globals';
import { usePath } from 'shared/helpers/hooks/usePath';
import { useAuth } from '@opendoor/auth-fe';
import { MONTH_DAY_AND_DAY_OF_WEEK_FORMAT, TIME_WITH_TZ_FORMAT } from 'shared/helpers/datetime';
import { ExternalLink } from 'platform/react-router';

export interface IAppointmentDateTimeCardStyleProps {
  flexProps?: FlexProps;
  iconCta?: boolean;
  onlyShowCta?: boolean;
}

export interface IAppointmentDateTimeCardProps extends IAppointmentDateTimeCardStyleProps {
  disableOnChangeCta?: boolean;
  earliestAvailability: ZonedDateTime | null;
  earliestAvailabilityError?: boolean;
  hasScheduledOrCompletedAppointment: boolean;
  hasUpcomingAppointment: boolean;
  loading?: boolean;
  onChange?: () => void;
  selectedDateTime: ZonedDateTime | null;
}

/**
 * This component renders a card UI for appointment date time with a CTA.
 * For displaying ORVA schedule card, please use <OfferReviewCard /> instead.
 */
export const AppointmentDateTimeCard: React.FC<IAppointmentDateTimeCardProps> = ({
  loading,
  earliestAvailability,
  earliestAvailabilityError,
  onChange,
  disableOnChangeCta,
  hasScheduledOrCompletedAppointment,
  hasUpcomingAppointment,
  selectedDateTime,
  ...styleProps
}) => {
  const { trackEvent } = useObservability();
  const displayTime = selectedDateTime || earliestAvailability;
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { isSellDirectQualified } = useQualificationContext();
  const {
    offer,
    shouldSeeAgentExperience,
    experimentationData: { isExclusiveOffersEnabled },
  } = useOfferContext();
  const { user } = useAuth();
  const { CXA_EMAIL } = useSupportContacts();

  const contactEmail = shouldSeeAgentExperience ? AGENT_ACQ_EMAIL : CXA_EMAIL;

  const onChangeClick = () => {
    const railsEnv = getRailsEnv();
    // show confirmation modal if OD user attempts to mutate data on production.
    if (railsEnv === 'production' && user?.hasOpendoorEmail) {
      setShowConfirmationModal(true);
    } else {
      onChange && onChange();
    }
  };

  if (loading) {
    return (
      <WrapperCard>
        <Loader />
      </WrapperCard>
    );
  }

  if (earliestAvailabilityError) {
    return (
      <>
        <WrapperCard>
          <Flex width="100%" alignItems="center" justifyContent="center">
            <H4>Looks like there's an issue</H4>
          </Flex>
        </WrapperCard>
        <Box mt={9} textAlign="center">
          <Text textAlign="center" mx={5} mb={7}>
            We ran into an issue scheduling your walkthrough.
          </Text>
          <Text as="span" textAlign="center" mx={5}>
            Please check back in a few minutes or email us at{' '}
            <ExternalLink
              analyticsName={
                isExclusiveOffersEnabled ? 'exclusive-offers-cxa-email' : 'legacy-cake-cxa-email'
              }
              href={`mailto:${contactEmail}`}
              aria-label="Contact us by email"
            >
              {contactEmail}
            </ExternalLink>{' '}
            and we’ll help you find a time.
          </Text>
        </Box>
      </>
    );
  } else if (!displayTime) {
    return (
      <>
        <WrapperCard>
          <Flex width="100%" alignItems="center" justifyContent="center">
            <H4>No available times</H4>
          </Flex>
        </WrapperCard>
        <Box mt={9} textAlign="center">
          <Text textAlign="center" mx={5} mb={7}>
            Looks like we don't have any available appointments within the next few weeks.
          </Text>
          <Text as="span" textAlign="center" mx={5}>
            Email{' '}
            <ExternalLink
              analyticsName={
                isExclusiveOffersEnabled ? 'exclusive-offers-cxa-email' : 'legacy-cake-cxa-email'
              }
              href={`mailto:${contactEmail}`}
              aria-label="Contact us by email"
            >
              {contactEmail}
            </ExternalLink>{' '}
            and we'll help you find a time.
          </Text>
        </Box>
      </>
    );
  }
  const renderCardTitle = () => {
    // For Exclusive Offers, AEP assignment isn't always correct yet,
    // so we opt not to show the AEP name or picture
    if (isExclusiveOffersEnabled || styleProps?.iconCta) {
      return <EarliestAvailableTimeLabel />;
    }
    if (isSellDirectQualified && offer?.experiencePartner?.name) {
      return <HomeAdvisorName fullName={offer.experiencePartner.name} />;
    }
    if (hasUpcomingAppointment) {
      return <CallScheduledLabel />;
    }
    if (!selectedDateTime) {
      return <EarliestAvailabilityLabel />;
    }
    return null;
  };

  return (
    <>
      <ConfirmationModal
        showConfirmationModal={showConfirmationModal}
        setShowConfirmationModal={setShowConfirmationModal}
        onCancel={() => trackEvent('click', 'cancel-proceed-from-appt-card')}
        onConfirm={() => {
          trackEvent('click', 'confirm-proceed-from-appt-card');
          onChange && onChange();
        }}
      />
      <WrapperCard {...styleProps}>
        <Flex
          width="100%"
          alignItems={'center'}
          justifyContent={styleProps?.iconCta ? 'space-between' : 'center'}
        >
          <Flex justifyContent="end" alignItems={'center'}>
            {isSellDirectQualified && offer?.experiencePartner?.photoUrl && (
              <Image
                src={offer.experiencePartner.photoUrl}
                alt={offer.experiencePartner.name}
                borderRadius="rounded"
                height={['48px', null, '56px']}
                maxWidth={['48px', null, '56px']}
                aria-hidden
              />
            )}
            <Flex flexDirection="column" ml={5}>
              {renderCardTitle()}
              <Flex>
                {styleProps?.iconCta ? (
                  <Text fontSize={['s0', null, 's2']} color="neutrals100" fontWeight="semibold">
                    {formatDateTime(displayTime)}
                  </Text>
                ) : (
                  <H4
                    color={hasScheduledOrCompletedAppointment ? 'neutrals100' : 'brand50'}
                    fontWeight="bold"
                  >
                    {formatDateTime(displayTime)}
                  </H4>
                )}
              </Flex>
            </Flex>
          </Flex>
          {styleProps?.iconCta && (
            <Button
              data-testid="edit-orva-icon-cta"
              analyticsName={
                isExclusiveOffersEnabled
                  ? 'exclusive-offers-edit-appointment-icon'
                  : 'legacy-cake-edit-orva-icon'
              }
              variant="icon"
              onClick={onChangeClick}
              aria-label="Change suggested appointment date and time"
              ml={6}
            >
              <Icon name="edit-filled" size={16} />
            </Button>
          )}
        </Flex>
        {!styleProps?.onlyShowCta && (
          <OnChangeCtaWithDivider
            hasUpcomingAppointment={hasUpcomingAppointment}
            onChange={onChange}
            isDisabled={disableOnChangeCta}
            onChangeClick={onChangeClick}
            isExclusiveOffersEnabled={isExclusiveOffersEnabled}
          />
        )}
      </WrapperCard>
    </>
  );
};

const HomeAdvisorName: React.FC<{ fullName: string }> = ({ fullName }) => (
  <Flex whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
    <Text fontSize="s0" fontWeight="bold" lineHeight={1}>
      {fullName}
    </Text>
  </Flex>
);

const CallScheduledLabel: React.FC = () => (
  <Flex>
    <ImgixImg width={18} height={18} src="d761993fcdea46d8aa734d928abf4dfe.svg" alt="" />
    <Text fontSize="s0" lineHeight="s00" fontWeight="bold" color="green50">
      Call scheduled
    </Text>
  </Flex>
);

const EarliestAvailabilityLabel: React.FC = () => (
  <Flex>
    <ImgixImg width={18} height={18} src="d761993fcdea46d8aa734d928abf4dfe.svg" alt="" />
    <Text fontSize="s0" lineHeight="s00" fontWeight="bold" color="green50">
      Earliest availability
    </Text>
  </Flex>
);

const EarliestAvailableTimeLabel: React.FC = () => (
  <Text fontSize={['s0', null, 's1']} color="neutrals100">
    Next available time
  </Text>
);

const OnChangeCtaWithDivider: React.FC<{
  hasUpcomingAppointment: boolean;
  onChange?: () => void;
  onChangeClick: () => void;
  isDisabled?: boolean;
  isExclusiveOffersEnabled?: boolean;
}> = ({
  onChange,
  isDisabled,
  onChangeClick,
  hasUpcomingAppointment,
  isExclusiveOffersEnabled,
}) => {
  const path = usePath();
  return (
    <>
      <Box pt={2}>
        <Divider />
      </Box>
      {onChange && (
        <>
          <Divider />
          <Flex justifyContent="center" alignItems="center" mt={4}>
            <Button
              aria-label="Change suggested appointment date and time"
              height="18px"
              fontSize="s000"
              fontWeight="bold"
              variant="link"
              onClick={onChangeClick}
              analyticsName={
                isExclusiveOffersEnabled
                  ? 'exclusive-offers-change-link-from-schedule-appointment'
                  : `legacy-cake-change-link-from-${path}${
                      hasUpcomingAppointment ? '-scheduled' : ''
                    }`
              }
              disabled={isDisabled}
            >
              CHANGE
            </Button>
          </Flex>
        </>
      )}
    </>
  );
};

const WrapperCard: React.FC = ({ children }) => (
  <Card
    variant="secondary"
    p={[7, 7, 7]}
    role="group"
    aria-label="Schedule a virtual assessment card"
  >
    {children}
  </Card>
);

// Example: Wed, Sep 16 at 11:50 AM PT
const formatDateTime = (dateTime: ZonedDateTime) => {
  return `${dateTime.format(MONTH_DAY_AND_DAY_OF_WEEK_FORMAT)} at ${dateTime.format(
    TIME_WITH_TZ_FORMAT,
  )}`;
};
